import PropTypes from 'prop-types';
import { useMemo, useCallback, useState } from 'react';
import produce from 'immer';
import TimelineAdvancedFiltersV2 from '../filter/TimelineAdvancedFiltersV2';
import TimelineEventsFilterV2 from '../filter/TimelineEventsFilterV2';
import { List } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { useSubjectContext } from 'crm-events-data';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { useFetchTimelineFilters, getSelectedFilters, useUpdateTimelineFilters, TimelineActions } from 'crm-events-data';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function TimelineFiltersV2({
  activityTabIsSelected
}) {
  const {
    objectType,
    subjectId
  } = useSubjectContext();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    data,
    error,
    updateQuery
  } = useFetchTimelineFilters({
    subjectId,
    objectTypeOrId: objectType
  });
  const filterGroups = useMemo(() => !data ? [] : data.crmObject.timeline.filterGroups, [data]);
  const selectedEventFilters = getSelectedFilters(filterGroups);
  const handleOpenChange = useCallback(({
    target: {
      value
    }
  }) => {
    setIsFilterOpen(value);
  }, [setIsFilterOpen]);
  const handlePopoverToggle = useCallback(() => {
    setIsFilterOpen(!isFilterOpen);
  }, [isFilterOpen, setIsFilterOpen]);
  const {
    updateTimelineFilters
  } = useUpdateTimelineFilters();
  const handleRemoveAllSelectedEvents = useCallback(() => {
    updateQuery(cachedFilters => {
      const allFiltersDeselected = produce(cachedFilters, draft => {
        draft.crmObject.timeline.filterGroups = draft.crmObject.timeline.filterGroups.map(filterGroup => Object.assign({}, filterGroup, {
          filters: filterGroup.filters.map(filter => Object.assign({}, filter, {
            selected: false
          }))
        }));
      });
      return allFiltersDeselected;
    });
  }, [updateQuery]);
  const handleUpdateSelectedEvents = useCallback(eventFilters => {
    // the BE does not allow persistance of no filters selected so we only update
    // no filters selected in the FE apollo cache without firing the mutation
    if (!eventFilters.length) {
      return handleRemoveAllSelectedEvents();
    }
    const eventFiltersList = ImmutableList(eventFilters);
    const requestPayload = ImmutableMap({
      eventOptions: eventFiltersList,
      objectType,
      subjectId
    });
    updateTimelineFilters({
      objectTypeOrId: objectType,
      cachedFiltersData: data,
      selectedEventFilters: eventFilters
    });
    return TimelineActions.updateSelectedEventFilters({
      objectType,
      newEventTypes: eventFiltersList,
      requestPayload
    });
  }, [data, handleRemoveAllSelectedEvents, objectType, subjectId, updateTimelineFilters]);
  const handleResetFilters = useCallback(() => {
    updateTimelineFilters({
      objectTypeOrId: objectType,
      cachedFiltersData: data,
      selectedEventFilters: []
    });
    CrmLogger.logRecordInteraction(objectType, {
      action: 'timeline filters cleared'
    });
    CrmLogger.log('timelineInteractions', {
      action: 'clear filter'
    });
  }, [objectType, data, updateTimelineFilters]);
  const renderedEventsFilter = useMemo(() => {
    return /*#__PURE__*/_jsx(TimelineEventsFilterV2, {
      error: error,
      filterGroups: filterGroups,
      handleOpenChange: handleOpenChange,
      handlePopoverToggle: handlePopoverToggle,
      handleUpdateSelectedEvents: handleUpdateSelectedEvents,
      isOpen: isFilterOpen,
      objectType: objectType
    });
  }, [error, filterGroups, handleOpenChange, handlePopoverToggle, handleUpdateSelectedEvents, isFilterOpen, objectType]);
  const renderedFilters = useMemo(() => {
    return /*#__PURE__*/_jsxs(List, {
      inline: true,
      wrapChildren: true,
      children: [renderedEventsFilter, /*#__PURE__*/_jsx(TimelineAdvancedFiltersV2, {
        error: error,
        objectType: objectType,
        onResetFilters: handleResetFilters,
        subjectId: subjectId,
        timelineFavoriteEvents: selectedEventFilters
      })]
    });
  }, [renderedEventsFilter, error, objectType, handleResetFilters, subjectId, selectedEventFilters]);
  if (!activityTabIsSelected) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    position: "relative",
    "data-selenium-test": "timeline-filter-container",
    inlineSize: "100%",
    flexDirection: "row",
    alignItems: "start",
    justifyContent: "space-between",
    paddingBlockStart: "0",
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: /*#__PURE__*/_jsx("h3", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "timeline.filters.accessibility.filterHeading"
        })
      })
    }), renderedFilters]
  }, "filters");
}
TimelineFiltersV2.propTypes = {
  activityTabIsSelected: PropTypes.bool.isRequired
};
export default TimelineFiltersV2;